// src/components/ui/file-upload.jsx
// 21st.dev "file-upload" adapted: PDF-only dropzone, controlled via onFilesChange,
// Grant.ai brand colors. Real upload progress lives in DataExtractor (XHR).
"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import clsx from "clsx";
import { UploadCloud, FileText, Trash2, CheckCircle } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

export default function FileUpload({ onFilesChange, maxFiles = 20 }) {
  const { darkMode } = useTheme();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (onFilesChange) onFilesChange(files);
  }, [files]);

  const handleFiles = (fileList) => {
    const pdfs = Array.from(fileList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of pdfs) {
        const dup = merged.some((x) => x.name === f.name && x.size === f.size);
        if (!dup && merged.length < maxFiles) merged.push(f);
      }
      return merged;
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onSelect = (e) => {
    if (e.target.files) handleFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const surface = darkMode
    ? {
        card: "bg-white/5 border-white/10",
        text: "text-zinc-100",
        sub: "text-zinc-400",
        item: "bg-white/5 hover:bg-white/10",
        accent: "text-[#6fc3d4]",
      }
    : {
        card: "bg-[#175e69]/5 border-[#175e69]/15",
        text: "text-gray-900",
        sub: "text-gray-500",
        item: "bg-gray-50 hover:bg-[#175e69]/5",
        accent: "text-[#175e69]",
      };

  return (
    <div className="w-full">
      {/* Drop zone */}
      <motion.div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        initial={false}
        animate={{
          scale: isDragging ? 1.01 : 1,
        }}
        transition={{ duration: 0.2 }}
        className={clsx(
          "group relative cursor-pointer rounded-2xl border p-8 text-center backdrop-blur transition-colors md:p-12",
          surface.card,
          isDragging && "ring-4 ring-[#2A8A9E]/30 border-[#2A8A9E]"
        )}
      >
        <div className="flex flex-col items-center gap-5">
          <motion.div
            animate={{ y: isDragging ? [-5, 0, -5] : 0 }}
            transition={{
              duration: 1.5,
              repeat: isDragging ? Infinity : 0,
              ease: "easeInOut",
            }}
            className="relative"
          >
            <motion.div
              animate={{
                opacity: isDragging ? [0.5, 1, 0.5] : 1,
                scale: isDragging ? [0.95, 1.05, 0.95] : 1,
              }}
              transition={{
                duration: 2,
                repeat: isDragging ? Infinity : 0,
                ease: "easeInOut",
              }}
              className="absolute -inset-4 rounded-full bg-[#2A8A9E]/15 blur-md"
              style={{ display: isDragging ? "block" : "none" }}
            />
            <UploadCloud
              className={clsx(
                "h-16 w-16 drop-shadow-sm transition-colors duration-300 md:h-20 md:w-20",
                isDragging ? "text-[#2A8A9E]" : clsx(surface.accent, "group-hover:text-[#2A8A9E]")
              )}
            />
          </motion.div>

          <div className="space-y-2">
            <h3 className={clsx("text-xl font-semibold md:text-2xl", surface.text)}>
              {isDragging
                ? "Drop PDFs here"
                : files.length
                  ? "Add more PDFs"
                  : "Upload your PDFs"}
            </h3>
            <p className={clsx("mx-auto max-w-md md:text-lg", surface.sub)}>
              {isDragging ? (
                <span className="font-medium text-[#2A8A9E]">Release to upload</span>
              ) : (
                <>
                  Drag &amp; drop invoices here, or{" "}
                  <span className="font-medium text-[#2A8A9E]">browse</span>
                </>
              )}
            </p>
            <p className={clsx("text-sm", surface.sub)}>PDF invoices &amp; statements only</p>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={onSelect}
            accept="application/pdf,.pdf"
          />
        </div>
      </motion.div>

      {/* Selected files list */}
      <div className="mt-8">
        <AnimatePresence>
          {files.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-3 flex items-center justify-between px-2"
            >
              <h3 className={clsx("text-lg font-semibold md:text-xl", surface.text)}>
                Selected PDFs ({files.length})
              </h3>
              {files.length > 1 && (
                <button
                  onClick={() => setFiles([])}
                  className={clsx(
                    "rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200 hover:text-red-600",
                    darkMode ? "bg-white/10 text-zinc-300" : "bg-gray-100 text-gray-700"
                  )}
                >
                  Clear all
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div
          className={clsx(
            "scrollbar-thin flex flex-col gap-3 overflow-y-auto pr-2",
            files.length > 3 && "max-h-96"
          )}
        >
          <AnimatePresence>
            {files.map((file) => (
              <motion.div
                key={`${file.name}-${file.lastModified}`}
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                className={clsx(
                  "flex items-center gap-4 rounded-xl px-4 py-4 shadow-sm transition-all duration-200",
                  surface.item
                )}
              >
                <div className="relative shrink-0">
                  <div
                    className={clsx(
                      "flex h-14 w-14 items-center justify-center rounded-lg border md:h-16 md:w-16",
                      darkMode ? "border-white/10 bg-white/5" : "border-[#175e69]/15 bg-white"
                    )}
                  >
                    <FileText className={clsx("h-7 w-7", surface.accent)} />
                  </div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={clsx(
                      "absolute -bottom-2 -right-2 rounded-full shadow-sm",
                      darkMode ? "bg-zinc-900" : "bg-white"
                    )}
                  >
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  </motion.div>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h4
                      className={clsx("truncate font-medium md:text-lg", surface.text)}
                      title={file.name}
                    >
                      {file.name}
                    </h4>
                    <Trash2
                      className={clsx(
                        "h-4 w-4 shrink-0 cursor-pointer transition-colors duration-200 hover:text-red-500",
                        darkMode ? "text-zinc-500" : "text-gray-400"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFiles((prev) =>
                          prev.filter(
                            (f) => !(f.name === file.name && f.lastModified === file.lastModified)
                          )
                        );
                      }}
                      aria-label="Remove file"
                    />
                  </div>
                  <span className={clsx("text-xs md:text-sm", surface.sub)}>
                    {formatFileSize(file.size)}
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
